import { useState } from "react";
import { Vendor } from "@po-management/types";
import { useApiClient } from "../../auth/useApiClient";

interface CreateVendorFormProps {
  onCreated: (vendor: Vendor) => void;
  onCancel: () => void;
}

interface FormState {
  name: string;
  address: string;
  vat: string;
  taxOffice: string;
  defaultPaymentTerms: string;
}

const EMPTY: FormState = { name: "", address: "", vat: "", taxOffice: "", defaultPaymentTerms: "60" };

export function CreateVendorForm({ onCreated, onCancel }: CreateVendorFormProps): JSX.Element {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { post } = useApiClient();

  function validate(): boolean {
    const e: Partial<FormState> = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.address.trim()) e.address = "Required";
    if (!form.vat.trim()) e.vat = "Required";
    if (!form.taxOffice.trim()) e.taxOffice = "Required";
    const pt = parseInt(form.defaultPaymentTerms, 10);
    if (isNaN(pt) || pt < 1) e.defaultPaymentTerms = "Must be at least 1";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setApiError(null);
    try {
      const res = await post("/api/vendors", {
        name: form.name.trim(),
        address: form.address.trim(),
        vat: form.vat.trim(),
        taxOffice: form.taxOffice.trim(),
        defaultPaymentTerms: parseInt(form.defaultPaymentTerms, 10),
      });
      const body = (await res.json()) as { success: boolean; data: Vendor; error?: string };
      if (!res.ok || !body.success) {
        setApiError(body.error ?? "Failed to create vendor");
        return;
      }
      onCreated(body.data);
    } catch {
      setApiError("Network error — please try again");
    } finally {
      setSubmitting(false);
    }
  }

  function field(label: string, key: keyof FormState, type = "text"): JSX.Element {
    return (
      <div style={{ marginBottom: 12 }}>
        <label htmlFor={key} style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{label}</label>
        <input
          id={key}
          type={type}
          value={form[key]}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          style={{ width: "100%", padding: "7px 8px", border: `1px solid ${errors[key] ? "red" : "#ccc"}`, borderRadius: 4, boxSizing: "border-box" }}
        />
        {errors[key] && <div style={{ color: "red", fontSize: 12, marginTop: 2 }}>{errors[key]}</div>}
      </div>
    );
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} style={{ background: "#f9f9fb", border: "1px solid #dde", borderRadius: 6, padding: 16, marginTop: 8 }}>
      <h4 style={{ margin: "0 0 12px", color: "#5B6A8A" }}>New Vendor</h4>
      {field("Vendor Name *", "name")}
      {field("Address *", "address")}
      {field("VAT Number (ΑΦΜ) *", "vat")}
      {field("Tax Office (ΔΟΥ) *", "taxOffice")}
      {field("Default Payment Terms (days) *", "defaultPaymentTerms", "number")}
      {apiError && <div style={{ color: "red", fontSize: 13, marginBottom: 8 }}>{apiError}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="submit"
          disabled={submitting}
          style={{ padding: "8px 16px", background: "#5B6A8A", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}
        >
          {submitting ? "Saving..." : "Save Vendor"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{ padding: "8px 16px", background: "#fff", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
